import React from 'react';
import { DEFAULT_COMPANY_INFO, resolveCompanyLogoUrl } from '../../../utils/companyInfo';
import { formatMoney } from './formatMoney';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const raw = String(dateString).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [year, month, day] = raw.slice(0, 10).split('-');
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getDocumentLabel = (transaction) => {
  const type = String(transaction?.trans_type || '').toUpperCase();
  const labels = {
    REM: 'Remittance',
    PAY: 'Payment',
    RIM: 'Reimbursement',
    CHG: 'Charge',
    ADJ: 'Adjustment',
    MOI: 'Monthly Invoice',
    JIN: 'Job Card Invoice',
    REV: 'Reversal'
  };

  return labels[type] || transaction?.short_desc || type || 'Owner Transaction';
};

const formatPaymentMethodLabel = (method) => {
  const value = String(method || '').trim().toUpperCase();
  const labels = {
    CASH: 'Cash',
    BANK_TRANSFER: 'Bank Transfer',
    MPESA: 'M-Pesa',
    CHEQUE: 'Cheque',
    CREDIT_CARD: 'Credit Card'
  };

  return labels[value] || value || '-';
};

const isReversedDocument = (transaction) => {
  const reversed = transaction?.reversed === true || transaction?.reversed === 1 || transaction?.reversed === '1';
  const reversalDoc = String(transaction?.reference_type || '').toUpperCase() === 'REVERSAL';
  return reversed || reversalDoc;
};

const normalizeDetailLines = (transaction) => {
  const rawLines = Array.isArray(transaction?.detail_lines)
    ? transaction.detail_lines
    : Array.isArray(transaction?.items)
      ? transaction.items
      : [];

  const plateCounts = rawLines.reduce((acc, line) => {
    const key = String(line?.plate_no || line?.plate || line?.car_plate_no || '-').trim().toUpperCase();
    if (!key || key === '-') {
      return acc;
    }
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return rawLines
    .map((line) => ({
      plate_no: line.plate_no || line.plate || line.car_plate_no || '-',
      gross_amount: Number(line.gross_amount ?? line.base_amount ?? line.amount ?? 0),
      discount_amount: Number(line.discount_amount ?? 0),
      prorated_amount: Number(line.prorated_amount ?? line.net_amount ?? line.amount ?? 0),
      days_counted: line.days_counted,
      allocation_date: line.allocation_date,
      invoice_year: line.invoice_year || line.details?.invoice_year,
      invoice_month: line.invoice_month || line.details?.invoice_month,
      invoice_period: line.invoice_period
        || line.detail_invoice_period
        || line._prorated_for_period
        || line.details?.invoice_period
        || line.details?.prorated_for_period
        || line.details?.detail_invoice_period,
      line_item_count: Math.max(
        1,
        Number(line.line_item_count || line.details?.line_item_count || plateCounts[String(line.plate_no || line.plate || line.car_plate_no || '-').trim().toUpperCase()] || 1)
      )
    }))
    .filter((line) => line.plate_no !== '-' || line.gross_amount || line.discount_amount || line.prorated_amount);
};

const splitDetailLinesForPages = (
  items,
  maxLinesPerContentPage = 15,
  footerPageCapacity = 5,
  minFooterLines = 4,
  minContentLinesPerPage = 10
) => {
  if (!items || items.length === 0) {
    return [[]];
  }

  const totalItems = items.length;

  if (totalItems <= maxLinesPerContentPage) {
    return [items];
  }

  const footerCount = Math.max(
    minFooterLines,
    Math.min(footerPageCapacity, totalItems)
  );
  const contentCount = totalItems - footerCount;
  const contentItems = items.slice(0, contentCount);
  const footerItems = items.slice(contentCount);

  const contentPageCount = Math.max(1, Math.ceil(contentItems.length / maxLinesPerContentPage));
  const pages = [];
  let cursor = 0;

  for (let pageIndex = 0; pageIndex < contentPageCount; pageIndex += 1) {
    const remainingContent = contentItems.length - cursor;
    const remainingPages = contentPageCount - pageIndex;
    const baseSize = Math.ceil(remainingContent / remainingPages);
    const take = Math.min(maxLinesPerContentPage, Math.max(1, baseSize));
    pages.push(contentItems.slice(cursor, cursor + take));
    cursor += take;
  }

  if (pages.length > 1) {
    // Keep the last content page reasonably full so the footer page does not become
    // visually heavier than the page before it.
    const minimumContentFill = Math.min(
      maxLinesPerContentPage,
      Math.max(1, Math.min(minContentLinesPerPage, Math.ceil(contentItems.length / Math.max(1, contentPageCount))))
    );

    for (let pageIndex = pages.length - 1; pageIndex > 0; pageIndex -= 1) {
      while (
        pages[pageIndex].length < minimumContentFill &&
        pages[pageIndex - 1].length > minimumContentFill
      ) {
        pages[pageIndex].unshift(pages[pageIndex - 1].pop());
      }
    }

    for (let pageIndex = 0; pageIndex < pages.length - 1; pageIndex += 1) {
      while (
        pages[pageIndex].length > pages[pageIndex + 1].length + 1 &&
        pages[pageIndex].length > minimumContentFill
      ) {
        pages[pageIndex + 1].unshift(pages[pageIndex].pop());
      }
    }
  }

  pages.push(footerItems);

  if (pages.length > 1) {
    const lastPage = pages[pages.length - 1];
    while (lastPage.length < minFooterLines) {
      const donorPage = pages[pages.length - 2];
      if (!donorPage || donorPage.length <= 1) {
        break;
      }

      lastPage.unshift(donorPage.pop());
    }
  }

  return pages.filter((page) => page.length > 0);
};

const OwnerTransactionPrintSheet = ({ transaction, companyInfo = DEFAULT_COMPANY_INFO }) => {
  if (!transaction) return null;

  const companyName = companyInfo?.company_name || DEFAULT_COMPANY_INFO.company_name;
  const companyAddress = companyInfo?.company_address || '';
  const companyPhone = companyInfo?.company_phone || '';
  const companyEmail = companyInfo?.company_email || '';
  const logoUrl = resolveCompanyLogoUrl(companyInfo);
  const documentLabel = getDocumentLabel(transaction);
  const reversedDocument = isReversedDocument(transaction);
  const detailLines = normalizeDetailLines(transaction);
  const detailGrossTotal = detailLines.reduce((sum, line) => sum + Number(line.gross_amount || 0), 0);
  const detailDiscountTotal = detailLines.reduce((sum, line) => sum + Number(line.discount_amount || 0), 0);
  const documentPeriod = transaction.invoice_period || (transaction.date
    ? String(transaction.date).slice(0, 7)
    : null);
  const getLinePeriod = (line) => {
    const linePeriod = line.invoice_period
      || (line.invoice_year && line.invoice_month ? `${line.invoice_year}-${String(line.invoice_month).padStart(2, '0')}` : null);
    return linePeriod || documentPeriod || null;
  };

  const docNo = String(transaction.id || '').split(':').pop() || transaction.id || '-';
  const subTotal = Number(transaction.gross_amount || detailGrossTotal || transaction.amount || 0);
  const discountAmount = Number(transaction.discount_amount || detailDiscountTotal || 0);
  const totalAmount = Number.isFinite(Number(transaction.amount))
    ? Number(transaction.amount)
    : Math.max(subTotal - discountAmount, 0);
  const paymentMade = Number(transaction.credit || 0) > 0 ? Number(transaction.credit || 0) : 0;
  const balanceDue = transaction.running_balance !== undefined && transaction.running_balance !== null
    ? Number(transaction.running_balance || 0)
    : Math.max(totalAmount - paymentMade, 0);
  const printedAt = new Date().toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const billToLines = [
    transaction.owner_name || transaction.owner_code || 'Unknown Owner',
    transaction.owner_code ? `Code: ${transaction.owner_code}` : '',
    transaction.owner_email ? `Email: ${transaction.owner_email}` : ''
  ].filter(Boolean);

  const pages = splitDetailLinesForPages(detailLines, 15, 5, 4, 10);

  const pageShellStyle = {
    width: '210mm',
    minHeight: '297mm',
    boxSizing: 'border-box',
    padding: '12mm',
    margin: '0 auto 8mm',
    backgroundColor: '#fff',
    color: '#111827',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden'
  };

  const renderLineRow = (line, index, rowNumber) => (
    <tr key={`${line.plate_no}-${rowNumber}-${index}`}>
      <td style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', verticalAlign: 'top' }}>{rowNumber}</td>
      <td style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', verticalAlign: 'top' }}>
        <div style={{ fontWeight: 700, color: '#2a2e30' }}>
          <span>{line.plate_no}</span>
          {getLinePeriod(line) && (
            <span style={{
              marginLeft: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: '999px',
              padding: '1px 6px',
              background: '#F3F4F6',
              color: '#6b7280',
              fontSize: '10px',
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: 'nowrap'
            }}>
              Period: {getLinePeriod(line)}
            </span>
          )}
        </div>
      </td>
      <td style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'right', verticalAlign: 'top' }}>
        {formatMoney(line.gross_amount)}
      </td>
      <td style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'right', verticalAlign: 'top' }}>
        {formatMoney(line.discount_amount)}
      </td>
      <td style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'right', verticalAlign: 'top' }}>
        {formatMoney(line.prorated_amount)}
      </td>
    </tr>
  );

  return (
    <div id="owner-transaction-print-sheet" style={{ width: '210mm', boxSizing: 'border-box', margin: '0 auto', backgroundColor: '#fff', color: '#111827', position: 'relative' }}>
      {pages.map((pageLines, pageIndex) => {
        const isLastPage = pageIndex === pages.length - 1;
        const rowOffset = pages.slice(0, pageIndex).reduce((sum, page) => sum + page.length, 0);

        return (
          <div
            key={`owner-transaction-page-${pageIndex}`}
            className="owner-transaction-pdf-page"
            style={{
              ...pageShellStyle,
              pageBreakAfter: isLastPage ? 'auto' : 'always',
              breakAfter: isLastPage ? 'auto' : 'page'
            }}
          >
            {reversedDocument && (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: '24% auto auto 50%',
                  transform: 'translateX(-50%) rotate(-20deg)',
                  fontSize: '56px',
                  fontWeight: 900,
                  letterSpacing: '0.12em',
                  color: 'rgba(220, 38, 38, 0.12)',
                  textTransform: 'uppercase',
                  zIndex: 0,
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap'
                }}
              >
                Reversed
              </div>
            )}

            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px',
                  borderBottom: reversedDocument ? '2px solid #dc2626' : '2px solid #FF5733',
                  paddingBottom: '12px',
                  marginBottom: '12px'
                }}
              >
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', minWidth: 0 }}>
                  <img
                    src={logoUrl}
                    alt="Company Logo"
                    crossOrigin="anonymous"
                    style={{ width: '54px', height: '54px', objectFit: 'contain', flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#2a2e30', lineHeight: 1.15 }}>
                      {companyName}
                    </h1>
                    <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontSize: '12px', color: reversedDocument ? '#dc2626' : '#FF5733', fontWeight: 700 }}>
                        {documentLabel} Document
                      </p>
                      {reversedDocument && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          borderRadius: '999px',
                          background: '#FEE2E2',
                          color: '#B91C1C',
                          fontSize: '10px',
                          fontWeight: 800,
                          padding: '2px 8px',
                          textTransform: 'uppercase'
                        }}>
                          Reversed
                        </span>
                      )}
                    </div>
                    {companyAddress && (
                      <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#6b7280', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                        {companyAddress}
                      </p>
                    )}
                    {(companyPhone || companyEmail) && (
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>
                        {[companyPhone, companyEmail].filter(Boolean).join(' | ')}
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ textAlign: 'right', minWidth: '172px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#6b7280' }}>
                    Document #{docNo}
                  </div>
                  {reversedDocument && (
                    <div style={{ marginTop: '4px', fontSize: '11px', fontWeight: 800, color: '#dc2626', textTransform: 'uppercase' }}>
                      Not a valid active document
                    </div>
                  )}
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                    Invoice Date
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#2a2e30' }}>
                    {formatDate(transaction.date)}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280' }}>
                    Balance Due
                  </div>
                  <div style={{ marginTop: '2px', fontSize: '20px', fontWeight: 800, color: balanceDue >= 0 ? '#1d4ed8' : '#dc2626' }}>
                    {formatMoney(balanceDue)}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.05fr 0.95fr',
                  gap: '12px',
                  marginBottom: '12px'
                }}
              >
                <div
                  style={{
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    background: reversedDocument ? '#fff1f2' : '#fff8f5'
                  }}
                >
                  <div style={{ fontSize: '12px', fontWeight: 800, color: '#2a2e30', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    Bill To
                  </div>
                  <div style={{ fontSize: '13px', lineHeight: 1.55, color: '#374151', marginTop: '8px' }}>
                    {billToLines.map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    background: '#f9fafb'
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px 12px', fontSize: '12px' }}>
                    <div style={{ color: '#6b7280' }}>Document Type</div>
                    <div style={{ fontWeight: 700, textAlign: 'right' }}>{documentLabel}</div>
                    <div style={{ color: '#6b7280' }}>Reference</div>
                    <div style={{ fontWeight: 700, textAlign: 'right' }}>{transaction.reference_id || transaction.id || '-'}</div>
                    <div style={{ color: '#6b7280' }}>Payment Method</div>
                    <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatPaymentMethodLabel(transaction.payment_method)}</div>
                    {transaction.payment_reference ? (
                      <>
                        <div style={{ color: '#6b7280' }}>Payment Reference</div>
                        <div style={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {transaction.payment_reference}
                        </div>
                      </>
                    ) : null}
                    <div style={{ color: '#6b7280' }}>Sub Total</div>
                    <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(subTotal)}</div>
                    <div style={{ color: '#6b7280' }}>Discount</div>
                    <div style={{ fontWeight: 700, textAlign: 'right', color: '#b45309' }}>{formatMoney(discountAmount)}</div>
                    <div style={{ color: '#6b7280', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>Total</div>
                    <div style={{ fontWeight: 800, textAlign: 'right', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>{formatMoney(totalAmount)}</div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#EDE4DA' }}>
                      <th style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'left', width: '8%' }}>#</th>
                      <th style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'left', width: '52%' }}>Item & Breakdown</th>
                      <th style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'right', width: '15%' }}>Gross</th>
                      <th style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'right', width: '15%' }}>Discount</th>
                      <th style={{ padding: '8px', border: '1px solid #d1d5db', fontSize: '12px', textAlign: 'right', width: '15%' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageLines.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: '12px', border: '1px solid #d1d5db', textAlign: 'center', color: '#6b7280' }}>
                          No line items
                        </td>
                      </tr>
                    ) : pageLines.map((line, index) => renderLineRow(line, index, rowOffset + index + 1))}
                  </tbody>
                </table>
              </div>

              {isLastPage && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.1fr 0.9fr',
                    gap: '14px'
                  }}
                >
                  <div>
                    <div
                      style={{
                        padding: '12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        background: '#fff'
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#2a2e30', marginBottom: '8px' }}>
                        Notes
                      </div>
                      <div style={{ fontSize: '12px', lineHeight: 1.55, color: '#374151', whiteSpace: 'pre-wrap', minHeight: '54px' }}>
                        {transaction.notes || transaction.description || 'Thanks for your business.'}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '10px',
                      background: '#f9fafb'
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 12px', fontSize: '12px' }}>
                      <div style={{ color: '#6b7280' }}>Sub Total</div>
                      <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(subTotal)}</div>
                      <div style={{ color: '#6b7280' }}>Discount</div>
                      <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(discountAmount)}</div>
                      <div style={{ color: '#6b7280' }}>Total</div>
                      <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(totalAmount)}</div>
                      <div style={{ color: '#6b7280' }}>Payment Made (-)</div>
                      <div style={{ fontWeight: 700, textAlign: 'right' }}>{formatMoney(paymentMade)}</div>
                      <div style={{ color: '#6b7280', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>Balance Due</div>
                      <div style={{ fontWeight: 700, textAlign: 'right', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>{formatMoney(balanceDue)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                marginTop: '12px',
                paddingTop: '10px',
                borderTop: '1px solid #e5e7eb',
                fontSize: '11px',
                color: '#6b7280',
                textAlign: 'center',
                lineHeight: 1.55
              }}
            >
              <div style={{ fontWeight: 700, color: '#2a2e30' }}>{companyName}</div>
              <div>Printed on {printedAt} | Page {pageIndex + 1} of {pages.length}</div>
              <div>Generated, powered by PawaFleet | Fleet management system</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OwnerTransactionPrintSheet;
